import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { ArrowLeft, ExternalLink, AlertCircle } from 'lucide-react'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'

const getPreviewUrl = (site) => {
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const SitePreview = () => {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)

  const { data: site, isLoading, isError } = useQuery(
    ['site-preview', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { staleTime: 60000, retry: 1 }
  )

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-0)' }}>
        <LoadingSpinner size="lg" text="Loading preview..." />
      </div>
    )
  }

  if (isError || !site) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '1rem', background: 'var(--bg-0)' }}>
        <AlertCircle style={{ width: '2.5rem', height: '2.5rem', color: 'var(--danger)' }} />
        <p className="text-soft">Failed to load site preview.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/client/sites')}>Go Back</button>
      </div>
    )
  }

  const previewUrl = getPreviewUrl(site)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top bar */}
      <div className="flex items-center gap-3" style={{ padding: '0.625rem 1rem', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/client/sites')}>
          <ArrowLeft style={{ width: '1rem', height: '1rem' }} />
          Back to Sites
        </button>
        <div style={{ width: '1px', height: '1rem', background: 'var(--border)' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="font-semibold truncate" style={{ color: 'var(--text)', fontSize: '0.875rem', margin: 0 }}>{site.name}</p>
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>Draft preview</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => window.open(previewUrl, '_blank')}>
          <ExternalLink style={{ width: '0.875rem', height: '0.875rem' }} />
          Open in new tab
        </button>
      </div>

      {/* iframe area */}
      <div style={{ flex: 1, position: 'relative', background: 'var(--bg-1)' }}>
        {!iframeLoaded && !iframeError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" text="Loading site..." />
          </div>
        )}
        {iframeError ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <AlertCircle style={{ width: '2.5rem', height: '2.5rem', color: 'var(--text-faint)' }} />
            <div style={{ textAlign: 'center' }}>
              <p className="font-medium text-soft" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Preview not available</p>
              <p className="text-faint" style={{ fontSize: '0.75rem', maxWidth: '24rem' }}>This site may not have been published yet.</p>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => window.open(previewUrl, '_blank')}>
              <ExternalLink style={{ width: '0.875rem', height: '0.875rem' }} />
              Try opening directly
            </button>
          </div>
        ) : (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title={`Preview: ${site.name}`}
            onLoad={() => setIframeLoaded(true)}
            onError={() => setIframeError(true)}
          />
        )}
      </div>
    </div>
  )
}

export default SitePreview
